import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { MusicNote, ArrowSquareOut, Megaphone, SpotifyLogo } from "@phosphor-icons/react";

// Convert any Spotify share URL (or already-embed URL) into the canonical embed URL.
function toEmbedUrl(raw) {
  if (!raw) return null;
  const s = String(raw).trim();
  const embedMatch = s.match(/open\.spotify\.com\/embed\/(playlist|album|track|show|episode)\/([A-Za-z0-9]+)/);
  if (embedMatch) return `https://open.spotify.com/embed/${embedMatch[1]}/${embedMatch[2]}?utm_source=generator`;
  const shareMatch = s.match(/open\.spotify\.com\/(playlist|album|track|show|episode)\/([A-Za-z0-9]+)/);
  if (shareMatch) return `https://open.spotify.com/embed/${shareMatch[1]}/${shareMatch[2]}?utm_source=generator`;
  const uriMatch = s.match(/^spotify:(playlist|album|track|show|episode):([A-Za-z0-9]+)/);
  if (uriMatch) return `https://open.spotify.com/embed/${uriMatch[1]}/${uriMatch[2]}?utm_source=generator`;
  return null;
}

export default function Playlist() {
  const [info, setInfo] = useState(null);
  // Detect iOS standalone PWA: iframe embeds frequently fail to bootstrap due
  // to Safari Tracking Prevention blocking Spotify's third-party cookies.
  // We fall back to a clean deep-link card so the page always works.
  const [hideEmbed, setHideEmbed] = useState(false);
  useEffect(() => {
    api.get("/info").then((r) => setInfo(r.data)).catch(() => setInfo({}));
    try {
      const isStandalone =
        window.navigator.standalone === true ||
        window.matchMedia("(display-mode: standalone)").matches;
      const isIOS = /iPad|iPhone|iPod/.test(window.navigator.userAgent);
      if (isStandalone && isIOS) setHideEmbed(true);
    } catch { /* noop */ }
  }, []);

  const url = info?.spotify_playlist_url || "";
  const embed = toEmbedUrl(url);

  return (
    <div data-testid="playlist-page" className="space-y-4">
      <div className="mb-2">
        <div className="font-mono-time text-xs uppercase text-[#7A828C] tracking-widest mb-1 flex items-center gap-1.5">
          <MusicNote size={12} weight="fill" /> Sound on
        </div>
        <h1 className="font-display text-3xl sm:text-4xl tracking-tight">The DFTB26 playlist</h1>
        <p className="text-sm text-[#5a5a5a] mt-2 leading-relaxed">
          The unofficial soundtrack to the conference - between sessions, on the
          way to the venue, or at 11pm when you&apos;re still arguing about
          sepsis. Hit play.
        </p>
      </div>

      {info === null ? (
        <div className="py-10 text-center text-[#7A828C]">Loading…</div>
      ) : !embed ? (
        <div data-testid="playlist-empty" className="p-6 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm text-center">
          <MusicNote size={32} weight="duotone" className="text-[#7A828C] mx-auto mb-3" />
          <p className="font-display text-base mb-1">No playlist yet</p>
          <p className="text-xs text-[#7A828C]">An organiser will share one shortly. Check back soon.</p>
        </div>
      ) : (
        <>
          {/* Primary CTA — always works, deep-links into the Spotify app on
              mobile and the web player on desktop. This is the hero now so a
              black-iframe edge case never blocks the experience. */}
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="playlist-open-spotify"
            className="flex items-center gap-4 p-5 bg-[#1DB954] text-[#000000] rounded-sm hover:brightness-110 transition-all shadow-sm"
          >
            <div className="w-12 h-12 grid place-items-center rounded-full bg-[#000000] flex-shrink-0">
              <SpotifyLogo size={26} weight="fill" className="text-[#1DB954]" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-lg leading-tight">Play on Spotify</div>
              <div className="text-xs text-[#000000]/80 mt-0.5">Opens the Spotify app · queue tracks, save offline</div>
            </div>
            <ArrowSquareOut size={20} className="flex-shrink-0" />
          </a>

          {/* Inline embed: shown on browsers + Android PWAs where it works
              reliably. Hidden inside iOS standalone where Safari blocks the
              Spotify embed JS. */}
          {!hideEmbed && (
            <div className="space-y-1">
              <div className="font-mono-time text-[10px] uppercase tracking-widest text-[#7A828C] px-1">Quick preview</div>
              <div
                data-testid="playlist-embed"
                className="bg-[#FFFFFF] rounded-sm overflow-hidden border border-[#E5E9F0]"
              >
                <iframe
                  title="DFTB26 conference playlist"
                  src={embed}
                  width="100%"
                  height="380"
                  frameBorder="0"
                  allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                  style={{ display: "block", border: 0, borderRadius: 4 }}
                />
              </div>
              <div className="text-[10px] text-[#7A828C] px-1">
                Player not loading? Tap <strong>Play on Spotify</strong> above.
              </div>
            </div>
          )}
        </>
      )}

      <Link
        to="/announcements"
        data-testid="playlist-news-link"
        className="flex items-center gap-3 p-4 bg-[#FFFFFF] border border-[#E5E9F0] rounded-sm hover:border-[#000000] transition-colors"
      >
        <div className="w-10 h-10 grid place-items-center rounded-sm border border-[#E5E9F0] flex-shrink-0">
          <Megaphone size={18} className="text-[#000000]" weight="duotone" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-base">Conference news</div>
          <div className="text-xs text-[#7A828C]">Announcements moved here - tap to see the full feed</div>
        </div>
        <span className="font-mono-time text-xs uppercase tracking-widest text-[#000000]">Open →</span>
      </Link>
    </div>
  );
}
